import * as Haptics from 'expo-haptics';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet } from 'react-native';
import { ExerciceCircleDots } from './ExerciceCircleDots';
import { GuidedBreathingModeEnum } from '../../types/breathing';
import { ParagraphXLRegular } from '../../config/designSystem/typography';
import { SoundHandle, playSound, preloadSound, unload } from '../../utils/sound/sound';
import { animate } from '../../utils/breathe/animate';
import { interpolateScale, interpolateTranslateY } from '../../utils/breathe/interpolate';
import { isWeb } from '../../config/designSystem/Layout';
import { loopAnimations } from '../../utils/breathe/loopAnimations';
import { useAvailableDimensions } from '../../hooks/ui/useAvailableDimensions';
import { useOnMount } from '../../hooks/useOnMount';
import { useStores } from '../../hooks/useStores';


interface Step {
  id: string;
  label: string;
  duration: number;
  showDots: boolean;
  skipped: boolean;
}

type Props = {
  steps: Step[];
  guidedBreathingMode: GuidedBreathingModeEnum;
  vibrationEnabled: boolean;
};


const fadeInAnimDuration = 400;

export const ExerciceCircle = (({
  steps,
  guidedBreathingMode,
  vibrationEnabled,
} : Props) => {
  const [showUpAnimVal] = useState(new Animated.Value(0));
  const [scaleAnimVal] = useState(new Animated.Value(0));
  const [textAnimVal] = useState(new Animated.Value(1));
  const [cirlceMinAnimVal] = useState(new Animated.Value(0));
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const activeSteps = steps.filter(x => !x.skipped);
  const currentStep = activeSteps[currentStepIndex];
  const [soundHold, setSoundHold] = useState<SoundHandle | undefined>(undefined);
  const [soundInhale, setSoundInhale] = useState<SoundHandle | undefined>(undefined);
  const [soundExhale, setSoundExhale] = useState<SoundHandle | undefined>(undefined);
  // Mirror of the three players for the unmount path. useOnMount's cleanup runs
  // with the MOUNT-time closure, where all three states are still undefined, so
  // unloading via the states leaked 3 native AudioPlayer instances per breathing
  // session (createAudioPlayer objects are not auto-released).
  const soundsRef = useRef<(SoundHandle | undefined)[]>([]);
  const [animationOnlyOnce, setAnimationOnlyOnce] = useState<boolean>(true);
  const rootStore = useStores();
  const labels = rootStore?.language?.translate?.MOBILE?.SNACKING?.BREATHE;
  const {width, height} = useAvailableDimensions();

  const circleSize = Math.min(width * 0.8, height *0.5);
  const [label, setLabel] = useState('');

  const holdPath = rootStore.technique.soundPath(guidedBreathingMode, 'Hold');
  const breatheInPath = rootStore.technique.soundPath(guidedBreathingMode, 'BreatheIn');
  const breatheOutPath = rootStore.technique.soundPath(guidedBreathingMode, 'BreatheOut');


  const animateStep = (toValue: number, duration: number) => {
    const textDuration = fadeInAnimDuration;
    return Animated.stagger(duration - textDuration, [
      Animated.parallel([
        animate(scaleAnimVal, {
          toValue: toValue,
          duration: duration,
        }),
        animate(textAnimVal, {
          toValue: 1,
          duration: textDuration,
        }),
      ]),
      animate(textAnimVal, {
        toValue: 0,
        duration: textDuration,
      }),
    ]);
  };

  const showCirlceMinAnimation = animate(cirlceMinAnimVal, {
    toValue: 1,
    duration: fadeInAnimDuration,
  });

  const hideCirlceMinAnimation = animate(cirlceMinAnimVal, {
    toValue: 0,
    duration: fadeInAnimDuration,
  });

  const showUpAnimation = animate(showUpAnimVal, {
    toValue: 1,
    duration: fadeInAnimDuration,
  });

  const onStepStart = (stepIndex: number) => {
    setCurrentStepIndex(stepIndex);
    const step = activeSteps[stepIndex];
    if (step.id === 'exhale') {
      playSound(soundExhale);
      setLabel(labels?.EXHALE);
      showCirlceMinAnimation.start();
    } else if (step.id === 'inhale') {
      playSound(soundInhale);
      setLabel(labels?.INHALE);
      hideCirlceMinAnimation.start();
    } else if (step.id === 'afterExhale') {
      playSound(soundHold);
      setLabel(labels?.HOLD);
      hideCirlceMinAnimation.start();
    } else if (step.id === 'afterInhale') {
      playSound(soundHold);
      setLabel(labels?.HOLD);
    }
    if (vibrationEnabled && !isWeb) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
      setTimeout(() => Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy), 100);
    }
  };

  const startAnimationSteps = () => {
    const createStepAnimations = () =>
      activeSteps.map(x =>
        animateStep(
          x.id === 'inhale' || x.id === 'afterInhale' ? 1 : 0,
          x.duration
        )
      );
    const stopLoop = loopAnimations(createStepAnimations, onStepStart);
    return stopLoop;
  };

  const preloadSounds = async () => {
    if(guidedBreathingMode !== GuidedBreathingModeEnum[GuidedBreathingModeEnum.disabled] ){
      setSoundHold(await preloadSound(holdPath));
      setSoundInhale(await preloadSound(breatheInPath));
      setSoundExhale(await preloadSound(breatheOutPath));
    }
  };



  React.useEffect(() => {
    let cleanUp = () => {};
    if((!!soundHold && !!soundInhale && !!soundExhale) ||
    guidedBreathingMode === GuidedBreathingModeEnum[GuidedBreathingModeEnum.disabled]) {
      if(animationOnlyOnce) {
        cleanUp = afterSoundMount();
      }
      setAnimationOnlyOnce(false);

    }
    return () => {
      cleanUp();
    };
  }, [soundHold, soundInhale, soundExhale]);


  useEffect(() => {
    soundsRef.current = [soundHold, soundInhale, soundExhale];
  }, [soundHold, soundInhale, soundExhale]);

  const unloadSounds = async () => {
    for (const sound of soundsRef.current) {
      await unload(sound);
    }
  };

  const afterSoundMount = () => {
    let cleanUpAnimationsSteps: () => void;
    showUpAnimation.start(({ finished }) => {
      if (finished) {
        cleanUpAnimationsSteps = startAnimationSteps();
      }
    });
    return () => {
      cleanUpAnimationsSteps && cleanUpAnimationsSteps();
      showUpAnimation.stop();
      showCirlceMinAnimation.stop();
      hideCirlceMinAnimation.stop();
    };
  };


  useOnMount(() => {
    preloadSounds();
    setAnimationOnlyOnce(true);
    return () =>  {
      unloadSounds();
    };

  });





  const containerAnimatedStyle = {
    opacity: showUpAnimVal.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
  };

  const circleAnimatedStyle = {
    transform: [
      interpolateScale(scaleAnimVal, {
        inputRange: [0, 1],
        outputRange: [0.6, 1],
      }),
    ],
  };

  const contentAnimatedStyle = {
    opacity: textAnimVal.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
    transform: [
      interpolateScale(scaleAnimVal, {
        inputRange: [0, 1],
        outputRange: [1, 1.3],
      }),
      interpolateTranslateY(textAnimVal, {
        inputRange: [0, 1],
        outputRange: [-8, 0],
      }),
    ],
  };

  const circleMinAnimatedStyle = {
    opacity: cirlceMinAnimVal.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
  };

  return (
    <Animated.View style={[styles.container, containerAnimatedStyle]}>
      <Animated.View style={[styles.circle,
        {    width: circleSize,
          height: circleSize,
          borderRadius: circleSize / 2},
        circleAnimatedStyle]} />
      <Animated.View style={[styles.circleMin,
        {
          width: circleSize,
          height: circleSize,
          borderRadius: circleSize / 2,
        }, circleMinAnimatedStyle]} />
      <Animated.View style={[styles.circleMax,
        {
          width: circleSize,
          height: circleSize,
          borderRadius: circleSize / 2,
        }]} />
      <Animated.View style={[styles.content, contentAnimatedStyle]}>
        <ParagraphXLRegular >{label}</ParagraphXLRegular>
        <ExerciceCircleDots
          visible={currentStep.showDots}
          numberOfDots={3}
          totalDuration={currentStep.duration}
        />
      </Animated.View>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  circle: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderColor: 'white',
    borderWidth: StyleSheet.hairlineWidth,
  },
  circleMin: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    transform: [{ scale: 0.6 }],
  },
  circleMax: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  content: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
});